import React, { useState, useEffect, useRef } from "react";
import config from "../../config/config";

const ProductsManager = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [message, setMessage] = useState("");
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "",
    image_url: "",
    icon: "",
    link: "",
    specifications: "",
    features: "",
    sort_order: 0,
    is_active: true,
  });
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);
  const dragItemRef = useRef(null);

  // Fetch products on component mount
  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await fetch(config.getAdminApiUrl("get_products.php"), {
        headers: {
          "X-API-Key": "ncg_admin_2024",
        },
      });
      const data = await response.json();

      if (data.success) {
        setProducts(data.data);
      } else {
        setMessage("Failed to fetch products: " + data.message);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      setMessage("Error fetching products");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      category: "",
      image_url: "",
      icon: "",
      link: "",
      specifications: "",
      features: "",
      sort_order: 0,
      is_active: true,
    });
    setEditingProduct(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setMessage("");

      const endpoint = editingProduct
        ? "update_product.php"
        : "create_product.php";

      const requestData = editingProduct
        ? { ...formData, id: editingProduct.id }
        : formData;

      const response = await fetch(config.getAdminApiUrl(endpoint), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": "ncg_admin_2024",
        },
        body: JSON.stringify(requestData),
      });

      const data = await response.json();

      if (data.success) {
        setMessage(data.message);
        setShowModal(false);
        resetForm();
        fetchProducts(); // Refresh the list
      } else {
        setMessage("Failed to save product: " + data.message);
      }
    } catch (error) {
      console.error("Error saving product:", error);
      setMessage("Error saving product");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      title: product.title,
      description: product.description || "",
      category: product.category || "",
      image_url: product.image_url || "",
      icon: product.icon || "",
      link: product.link || "",
      specifications: product.specifications || "",
      features: product.features || "",
      sort_order: product.sort_order || 0,
      is_active: product.is_active,
    });
    setShowModal(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm("Are you sure you want to delete this product?")) {
      return;
    }

    try {
      setLoading(true);
      setMessage("");

      const response = await fetch(config.getAdminApiUrl("delete_product.php"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": "ncg_admin_2024",
        },
        body: JSON.stringify({ id: productId }),
      });

      const data = await response.json();

      if (data.success) {
        setMessage(data.message);
        fetchProducts(); // Refresh the list
      } else {
        setMessage("Failed to delete product: " + data.message);
      }
    } catch (error) {
      console.error("Error deleting product:", error);
      setMessage("Error deleting product");
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  // Drag and drop handlers
  const handleDragStart = (e, product) => {
    setDraggedItem(product);
    dragItemRef.current = product;
    e.dataTransfer.effectAllowed = "move";
    e.target.style.opacity = "0.5";
  };

  const handleDragOver = (e, product) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragItemRef.current && dragItemRef.current.id !== product.id) {
      setDragOverItem(product);
    }
  };

  const handleDragLeave = (e) => {
    setDragOverItem(null);
  };

  const handleDrop = async (e, product) => {
    e.preventDefault();
    setDragOverItem(null);

    if (!dragItemRef.current || dragItemRef.current.id === product.id) {
      return;
    }

    try {
      setLoading(true);
      setMessage("");

      // Create new order array
      const newOrder = [...products];
      const draggedIndex = newOrder.findIndex(
        (p) => p.id === dragItemRef.current.id
      );
      const dropIndex = newOrder.findIndex((p) => p.id === product.id);

      // Remove dragged item and insert at new position
      const [draggedProduct] = newOrder.splice(draggedIndex, 1);
      newOrder.splice(dropIndex, 0, draggedProduct);

      const response = await fetch(config.getAdminApiUrl("update_product_order.php"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": "ncg_admin_2024",
        },
        body: JSON.stringify({ products: newOrder }),
      });

      const data = await response.json();

      if (data.success) {
        setMessage("Product order updated successfully");
        fetchProducts(); // Refresh the list
      } else {
        setMessage("Failed to update product order: " + data.message);
      }
    } catch (error) {
      console.error("Error updating product order:", error);
      setMessage("Error updating product order");
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = "1";
    setDraggedItem(null);
    setDragOverItem(null);
    dragItemRef.current = null;
  };

  return (
    <div className="content-manager">
      <div className="content-header">
        <h2>Products Management</h2>
        <p>
          Manage the products displayed in the "Our Products" section.{" "}
          <strong>Top 9 products will be shown on the homepage.</strong>
        </p>
        <p className="text-muted">
          💡 <strong>Tip:</strong> Drag and drop rows to reorder products. The
          first 9 products will appear on the homepage.
        </p>
      </div>

      {message && (
        <div
          className={`alert ${
            message.includes("successfully") ? "alert-success" : "alert-danger"
          }`}
        >
          {message}
        </div>
      )}

      <div className="content-actions">
        <button
          className="btn btn-primary"
          onClick={openAddModal}
          disabled={loading}
        >
          <i className="fas fa-plus me-2"></i>
          Add New Product
        </button>
      </div>

      {loading && products.length === 0 ? (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="products-table">
            <thead>
              <tr>
                <th width="50">⋮⋮</th>
                <th>Icon</th>
                <th>Title</th>
                <th>Category</th>
                <th>Description</th>
                <th>Order</th>
                <th>Status</th>
                <th>Link</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product, index) => (
                <tr
                  key={product.id}
                  className={`${!product.is_active ? "inactive-row" : ""} ${
                    dragOverItem && dragOverItem.id === product.id
                      ? "drag-over"
                      : ""
                  } ${index < 9 ? "top-nine" : ""}`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, product)}
                  onDragOver={(e) => handleDragOver(e, product)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, product)}
                  onDragEnd={handleDragEnd}
                >
                  <td className="drag-handle-cell">
                    <div className="drag-handle">
                      <i className="fas fa-grip-vertical"></i>
                    </div>
                  </td>
                  <td className="product-icon-cell">
                    <div className="product-icon">
                      <i className={product.icon || "fas fa-box"}></i>
                    </div>
                  </td>
                  <td className="product-title-cell">
                    <strong>{product.title}</strong>
                  </td>
                  <td className="product-category-cell">
                    <span className="category-badge">{product.category}</span>
                  </td>
                  <td className="product-description-cell">
                    <div className="description-text">
                      {product.description}
                    </div>
                  </td>
                  <td className="product-order-cell">
                    <span className="order-badge">{product.sort_order}</span>
                  </td>
                  <td className="product-status-cell">
                    <span
                      className={`status-badge ${
                        product.is_active ? "active" : "inactive"
                      }`}
                    >
                      {product.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="product-link-cell">
                    {product.link ? (
                      <a
                        href={product.link}
                        target="_blank"
                        rel="noopener"
                        className="link-button"
                        title="View Link"
                      >
                        <i className="fas fa-external-link-alt"></i>
                      </a>
                    ) : (
                      <span className="no-link">-</span>
                    )}
                  </td>
                  <td className="product-actions-cell">
                    <div className="action-buttons">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handleEdit(product)}
                        disabled={loading}
                        title="Edit Product"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(product.id)}
                        disabled={loading}
                        title="Delete Product"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Product Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingProduct ? "Edit Product" : "Add New Product"}</h3>
              <button className="btn-close" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Product Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="form-control"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Category</label>
                  <input
                    type="text"
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="e.g., Steel Products, Fabrication"
                  />
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    className="form-control"
                    rows="3"
                  />
                </div>

                <div className="form-group">
                  <label>Icon Class (FontAwesome)</label>
                  <input
                    type="text"
                    name="icon"
                    value={formData.icon}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="e.g., fas fa-box"
                  />
                </div>

                <div className="form-group">
                  <label>Image URL</label>
                  <input
                    type="url"
                    name="image_url"
                    value={formData.image_url}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="https://example.com/image.jpg"
                  />
                </div>

                <div className="form-group">
                  <label>Link URL</label>
                  <input
                    type="url"
                    name="link"
                    value={formData.link}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="https://example.com"
                  />
                </div>

                <div className="form-group">
                  <label>Specifications</label>
                  <textarea
                    name="specifications"
                    value={formData.specifications}
                    onChange={handleInputChange}
                    className="form-control"
                    rows="2"
                    placeholder="Technical specifications..."
                  />
                </div>

                <div className="form-group">
                  <label>Features</label>
                  <textarea
                    name="features"
                    value={formData.features}
                    onChange={handleInputChange}
                    className="form-control"
                    rows="2"
                    placeholder="Key features..."
                  />
                </div>

                <div className="form-group">
                  <label>Sort Order</label>
                  <input
                    type="number"
                    name="sort_order"
                    value={formData.sort_order}
                    onChange={handleInputChange}
                    className="form-control"
                    min="0"
                  />
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleInputChange}
                    />
                    Active
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <i className="fas fa-spinner fa-spin me-2"></i>
                      Saving...
                    </>
                  ) : (
                    "Save Product"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style jsx>{`
        .table-responsive {
          overflow-x: auto;
          margin-top: 1.5rem;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .products-table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          font-size: 0.9rem;
        }

        .products-table th {
          background: #f8f9fa;
          color: #495057;
          font-weight: 600;
          text-align: left;
          padding: 1rem;
          border-bottom: 2px solid #dee2e6;
          font-size: 0.85rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .products-table td {
          padding: 1rem;
          border-bottom: 1px solid #e9ecef;
          vertical-align: middle;
        }

        .products-table tbody tr {
          transition: background-color 0.2s ease;
        }

        .products-table tbody tr:hover {
          background-color: #f8f9fa;
        }

        .products-table tbody tr.inactive-row {
          opacity: 0.6;
          background-color: #f8f9fa;
        }

        .products-table tbody tr.inactive-row:hover {
          background-color: #e9ecef;
        }

        .products-table tbody tr.top-nine {
          border-left: 4px solid #28a745;
        }

        .products-table tbody tr.drag-over {
          background-color: #e3f2fd !important;
          border-top: 2px solid #2196f3;
        }

        .products-table tbody tr {
          cursor: grab;
          transition: all 0.2s ease;
        }

        .products-table tbody tr:active {
          cursor: grabbing;
        }

        .drag-handle-cell {
          width: 50px;
          text-align: center;
          padding: 0.5rem !important;
        }

        .drag-handle {
          color: #6c757d;
          cursor: grab;
          font-size: 1.2rem;
          padding: 0.25rem;
          border-radius: 4px;
          transition: all 0.2s ease;
        }

        .drag-handle:hover {
          color: #495057;
          background-color: #f8f9fa;
        }

        .drag-handle:active {
          cursor: grabbing;
        }

        .product-icon-cell {
          width: 80px;
          text-align: center;
        }

        .product-icon {
          width: 40px;
          height: 40px;
          background: #f8f9ff;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #007bff;
          font-size: 1.2rem;
          margin: 0 auto;
        }

        .product-title-cell {
          font-weight: 600;
          color: #2c3e50;
          min-width: 150px;
        }

        .product-category-cell {
          width: 120px;
        }

        .category-badge {
          background: #e3f2fd;
          color: #1976d2;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-size: 0.8rem;
          font-weight: 500;
        }

        .product-description-cell {
          max-width: 300px;
        }

        .description-text {
          color: #6c757d;
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .product-order-cell {
          text-align: center;
          width: 80px;
        }

        .order-badge {
          background: #e9ecef;
          color: #495057;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-size: 0.8rem;
          font-weight: 500;
        }

        .product-status-cell {
          text-align: center;
          width: 100px;
        }

        .status-badge {
          padding: 0.25rem 0.75rem;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .status-badge.active {
          background: #d4edda;
          color: #155724;
        }

        .status-badge.inactive {
          background: #f8d7da;
          color: #721c24;
        }

        .product-link-cell {
          text-align: center;
          width: 80px;
        }

        .link-button {
          color: #007bff;
          text-decoration: none;
          padding: 0.5rem;
          border-radius: 4px;
          transition: all 0.2s ease;
          display: inline-block;
        }

        .link-button:hover {
          background: #f8f9ff;
          color: #0056b3;
          transform: scale(1.1);
        }

        .no-link {
          color: #6c757d;
          font-style: italic;
        }

        .product-actions-cell {
          text-align: center;
          width: 120px;
        }

        .action-buttons {
          display: flex;
          gap: 0.5rem;
          justify-content: center;
        }

        .action-buttons .btn {
          padding: 0.375rem 0.75rem;
          border-radius: 4px;
          font-size: 0.8rem;
          transition: all 0.2s ease;
        }

        .action-buttons .btn:hover {
          transform: translateY(-1px);
        }

        .btn-outline-primary {
          border-color: #007bff;
          color: #007bff;
        }

        .btn-outline-primary:hover {
          background: #007bff;
          color: white;
        }

        .btn-outline-danger {
          border-color: #dc3545;
          color: #dc3545;
        }

        .btn-outline-danger:hover {
          background: #dc3545;
          color: white;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          backdrop-filter: blur(2px);
        }

        .modal-content {
          background: white;
          border-radius: 16px;
          width: 90%;
          max-width: 700px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
          animation: modalSlideIn 0.3s ease-out;
        }

        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: translateY(-20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .modal-header {
          padding: 1.5rem 1.5rem 1rem;
          border-bottom: 1px solid #e9ecef;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .modal-header h3 {
          margin: 0;
          color: #2c3e50;
          font-size: 1.5rem;
          font-weight: 600;
        }

        .modal-body {
          padding: 1.5rem;
        }

        .modal-footer {
          padding: 1rem 1.5rem 1.5rem;
          border-top: 1px solid #e9ecef;
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
          background: #f8f9fa;
          border-radius: 0 0 16px 16px;
        }

        .btn-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #6c757d;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 6px;
          transition: all 0.2s ease;
        }

        .btn-close:hover {
          background: #e9ecef;
          color: #495057;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          font-weight: 500;
          color: #495057;
        }

        .checkbox-label input[type="checkbox"] {
          margin: 0;
          width: 18px;
          height: 18px;
          accent-color: #007bff;
        }

        .form-group {
          margin-bottom: 1.25rem;
        }

        .form-group label {
          display: block;
          margin-bottom: 0.5rem;
          font-weight: 500;
          color: #495057;
        }

        .form-control {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid #ced4da;
          border-radius: 8px;
          font-size: 0.95rem;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }

        .form-control:focus {
          outline: none;
          border-color: #007bff;
          box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
        }

        .btn {
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          font-weight: 500;
          transition: all 0.2s ease;
          border: none;
          cursor: pointer;
        }

        .btn-primary {
          background: #007bff;
          color: white;
        }

        .btn-primary:hover {
          background: #0056b3;
          transform: translateY(-1px);
        }

        .btn-secondary {
          background: #6c757d;
          color: white;
        }

        .btn-secondary:hover {
          background: #545b62;
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none !important;
        }
      `}</style>
    </div>
  );
};

export default ProductsManager;
